import React from 'react';
import { graphql } from 'gatsby';
import get from 'lodash/get';
import * as Yup from 'yup';
import { Container, Row, Col, Alert } from 'reactstrap';
import { Formik, Form as FormikForm, Field } from 'formik';
import axios from 'axios';
import {
  Box,
  Flex,
  Heading,
  Text,
  Input,
  Button,
} from '@hausgold/designsystem';
import Header from '../components/Header';
import SEO from '../components/SEO';
import Layout from '../components/Layout';
import Link from '../components/Link';
import GoogleRating from '../components/GoogleRating';
import {
  analyticsTrack,
  EVENT_OBJECT,
  EVENT_VERB,
  eventBuilder,
} from '../utils/tracking';

const CooperationSchema = Yup.object().shape({
  company: Yup.string().required('Bitte geben Sie Ihr Maklerbüro an.'),
  name: Yup.string().required(
    'Bitte geben Sie den Namen des Antragstellers an.'
  ),
  email: Yup.string()
    .email('Bitte geben Sie eine richtige E-Mail-Adresse an.')
    .required('Bitte geben Sie Ihre E-Mail-Adresse an.'),
  zip: Yup.number()
    .min(1067, 'Bitte geben seine eine gültige Postleitzahl an.')
    .max(99999, 'Bitte geben seine eine gültige Postleitzahl an.')
    .required('Bitte geben Sie Ihre Postleitzahl an.'),
  phone: Yup.number()
    .min(3, 'Bitte geben seine eine gültige Telefonnummer an.')
    .required('Bitte geben Sie Ihre Telefonnumer an.'),
  website: Yup.string(),
  interest: Yup.string()
    .oneOf(['objektbörse', 'heyemi', 'beides'])
    .required('Bitte geben Sie an für welchen Service Sie sich interessieren.'),
});

/*
 * Selectable interest options rendered as cards. The `value`s are kept in sync
 * with the backend (getform / CRM) while the labels follow the new branding.
 */
const INTEREST_OPTIONS = [
  {
    value: 'objektbörse',
    title: 'Objektbörse',
    description: 'Verkaufsbereite Eigentümer finden',
  },
  {
    value: 'autopilot',
    title: 'Autopilot',
    description: 'Kontakte automatisch qualifizieren und terminieren lassen',
  },
  {
    value: 'beides',
    title: 'Beide zusammen',
    description: 'Kontakte finden und automatisch bearbeiten lassen',
    recommended: true,
  },
];

// Shared style for the text inputs so every field looks identical.
const fieldStyles = {
  borderRadius: '10px',
  borderColor: 'blackAlpha.300',
  focusBorderColor: 'blue.400',
  fontSize: '16px',
  height: 'auto',
  py: '13px',
  px: '14px',
};

// Small helper to keep the label markup consistent across all fields.
const FieldLabel = ({ children, optional }) => (
  <Text
    as="label"
    display="block"
    fontSize="14px"
    fontWeight={700}
    color="gray.600"
    mt="18px"
    mb="7px"
  >
    {children}
    {optional && (
      <Text as="span" fontWeight={400} color="gray.400">
        {' '}
        · optional
      </Text>
    )}
  </Text>
);

const FieldError = ({ children }) => (
  <Box color="red.400" fontSize="14px" mt="0.25rem" width="100%">
    {children}
  </Box>
);

const Kooperation = ({ data, location }) => {
  const page = get(data, 'contentfulPage');
  const site = get(data, 'site');
  const googleRating = get(data, 'googleRating');

  const breadcrumbs = [
    {
      title: 'Startseite',
      url: `${site.siteMetadata.siteUrl}/`,
    },
    {
      title: page.title,
      url: `${site.siteMetadata.siteUrl}/${page.slug}/`,
    },
  ];

  return (
    <Layout location={location} cookieConsent header={<Header />}>
      <SEO
        title={page.metaTitle || page.headline}
        canonical={page.canonical}
        type={page.type}
        breadcrumbs={breadcrumbs}
        description={page.metaDescription}
        indexable={page.indexable}
      />

      <Container>
        <Row className="mt-6 mb-6 align-items-start">
          {/* LEFT: value proposition & trust */}
          <Col xs="12" lg="6" className="mb-5">
            <Text
              as="span"
              display="block"
              fontSize="14px"
              letterSpacing="0.16em"
              textTransform="uppercase"
              fontWeight={700}
              color="blue.400"
              mb="14px"
            >
              Für Makler
            </Text>
            <Heading
              as="h2"
              fontSize={{ base: '28px', lg: '34px' }}
              fontWeight={700}
              color="gray.600"
            >
              Ihr Zugang zu HAUSGOLD.
            </Heading>
            <Text fontSize="19px" color="gray.400" mt="16px" mb="30px">
              Objektbörse und Autopilot, ein Zugang für Ihre Akquise.
            </Text>

            <Flex
              as="ul"
              direction="column"
              gap={{ base: '8px', md: '18px' }}
              listStyleType="none"
              m="0"
              p="0"
            >
              {[
                'Kostenlos und unverbindlich, ohne Vertragsbindung',
                'Freischaltung meist innerhalb einer Stunde',
                'Persönliche Betreuung, kein Callcenter',
              ].map((point) => (
                <Flex as="li" key={point} align="flex-start" gap="12px">
                  <Flex
                    align="center"
                    justify="center"
                    flex="none"
                    w="22px"
                    h="22px"
                    borderRadius="50%"
                    bg="blue.200"
                    color="blue.400"
                    fontSize="14px"
                    fontWeight={700}
                    mt="1px"
                  >
                    ✓
                  </Flex>
                  <Text as="span" fontSize="16px" color="gray.600" mb="0">
                    {point}
                  </Text>
                </Flex>
              ))}
            </Flex>

            <Flex
              gap="12px"
              rowGap="16px"
              wrap="wrap"
              align="center"
              justify={{ base: 'space-around', lg: 'space-between' }}
              mt="34px"
              pt="26px"
              borderTop="1px solid"
              borderTopColor="gray.100"
            >
              <Box>
                <Text
                  fontSize="20px"
                  fontWeight={700}
                  color="gray.600"
                  lineHeight="1"
                  mb="0"
                >
                  seit 2014
                </Text>
                <Text fontSize="14px" color="gray.400" mb="0">
                  am Markt
                </Text>
              </Box>
              <Box>
                <Text
                  fontSize="20px"
                  fontWeight={700}
                  color="gray.600"
                  lineHeight="1"
                  mb="0"
                >
                  3.000+
                </Text>
                <Text fontSize="14px" color="gray.400" mb="0">
                  Makler vertrauen uns
                </Text>
              </Box>
              {/* Wrapper puts the rating on its own centered row below `sm`. */}
              <Box
                flexBasis={{ base: '100%', sm: 'auto' }}
                display="flex"
                justifyContent="center"
              >
                <GoogleRating
                  rating={googleRating?.rating}
                  userRatingCount={googleRating?.userRatingCount}
                  googleMapsUri={googleRating?.googleMapsUri}
                />
              </Box>
            </Flex>
          </Col>

          {/* RIGHT: registration form */}
          <Col xs="12" lg="6">
            <Box
              bg="#fff"
              borderWidth="1px"
              borderStyle="solid"
              borderColor="blackAlpha.300"
              borderRadius="20px"
              p={{ base: '24px', md: '36px 34px' }}
              boxShadow="0 24px 60px rgba(46,45,56,.10)"
            >
              <Heading as="h3" fontSize="22px" color="gray.600" mb="22px">
                In zwei Minuten registriert.
              </Heading>

              <Formik
                initialValues={{
                  company: '',
                  name: '',
                  email: '',
                  zip: '',
                  website: '',
                  phone: '',
                  interest: 'beides',
                }}
                validationSchema={CooperationSchema}
                onSubmit={async (values, { resetForm, setStatus }) => {
                  axios
                    .post(
                      'https://getform.io/f/823a2176-5196-4df4-944d-942951b77554',
                      values,
                      { headers: { Accept: 'application/json' } }
                    )
                    .then(() => {
                      resetForm();
                      setStatus({ success: true });

                      analyticsTrack({
                        event: eventBuilder(
                          EVENT_OBJECT.formSubmit,
                          EVENT_VERB.interacted
                        ),
                        params: {
                          label: 'Kostenlosen Zugang anfragen',
                          element_type: 'SubmitButton',
                        },
                      });
                    })
                    .catch(() => {
                      setStatus({ success: false });
                    });
                }}
              >
                {({ errors, values, touched, isSubmitting, status }) => (
                  <FormikForm>
                    {status && status.success && (
                      <Alert color="success">Danke für Ihre Nachricht.</Alert>
                    )}
                    {status && !status.success && (
                      <Alert color="danger">
                        Leider ist ein Fehler aufgetreten. Bitte senden Sie für
                        Ihre Anfrage eine E-Mail an{' '}
                        <Link
                          to="mailto:accountmanagement@hausgold.de"
                          eventLabel="accountmanagement@hausgold.de"
                        >
                          accountmanagement@hausgold.de
                        </Link>{' '}
                        .
                      </Alert>
                    )}

                    <FieldLabel>Woran sind Sie interessiert?</FieldLabel>
                    <Flex direction="column" gap="10px">
                      {INTEREST_OPTIONS.map((option) => {
                        const checked = values.interest === option.value;
                        return (
                          <Flex
                            as="label"
                            key={option.value}
                            position="relative"
                            align="flex-start"
                            gap="12px"
                            p="14px 16px"
                            cursor="pointer"
                            borderRadius="12px"
                            borderWidth="1.5px"
                            borderStyle="solid"
                            borderColor={
                              checked ? 'blue.400' : 'blackAlpha.300'
                            }
                            bg={checked ? 'blue.200' : '#fff'}
                            transition="0.15s"
                            _hover={{ borderColor: 'blue.400' }}
                          >
                            <Field
                              type="radio"
                              name="interest"
                              value={option.value}
                              style={{
                                position: 'absolute',
                                opacity: 0,
                                pointerEvents: 'none',
                              }}
                            />
                            {option.recommended && (
                              <Box
                                position="absolute"
                                top="-9px"
                                right="14px"
                                bg="blue.400"
                                color="#fff"
                                fontSize="12px"
                                letterSpacing="0.08em"
                                textTransform="uppercase"
                                px="9px"
                                py="3px"
                                borderRadius="100px"
                              >
                                Empfohlen
                              </Box>
                            )}
                            <Box
                              flex="none"
                              w="20px"
                              h="20px"
                              mt="1px"
                              borderRadius="50%"
                              borderWidth="2px"
                              borderStyle="solid"
                              borderColor={
                                checked ? 'blue.400' : 'blackAlpha.300'
                              }
                              bg={checked ? 'blue.400' : 'transparent'}
                              boxShadow={
                                checked ? 'inset 0 0 0 3px #fff' : 'none'
                              }
                              transition="0.15s"
                            />
                            <Box>
                              <Text
                                as="span"
                                display="block"
                                fontSize="15px"
                                fontWeight={700}
                                color="gray.600"
                                lineHeight="1.2"
                                mb="2px"
                              >
                                {option.title}
                              </Text>
                              <Text
                                as="span"
                                fontSize="14px"
                                color="gray.400"
                                lineHeight="1.3"
                              >
                                {option.description}
                              </Text>
                            </Box>
                          </Flex>
                        );
                      })}
                    </Flex>
                    {!!errors.interest && touched.interest && (
                      <FieldError>{errors.interest}</FieldError>
                    )}

                    <FieldLabel>Maklerbüro</FieldLabel>
                    <Input
                      as={Field}
                      type="text"
                      name="company"
                      id="company"
                      autoComplete="organization"
                      isInvalid={!!errors.company && touched.company}
                      {...fieldStyles}
                    />
                    {!!errors.company && touched.company && (
                      <FieldError>{errors.company}</FieldError>
                    )}

                    <Row form>
                      <Col xs="12" sm="6">
                        <FieldLabel>Ihr Name</FieldLabel>
                        <Input
                          as={Field}
                          type="text"
                          name="name"
                          id="name"
                          autoComplete="name"
                          isInvalid={!!errors.name && touched.name}
                          {...fieldStyles}
                        />
                        {!!errors.name && touched.name && (
                          <FieldError>{errors.name}</FieldError>
                        )}
                      </Col>
                      <Col xs="12" sm="6">
                        <FieldLabel>Postleitzahl</FieldLabel>
                        <Input
                          as={Field}
                          type="text"
                          name="zip"
                          id="zip"
                          inputMode="numeric"
                          autoComplete="postal-code"
                          isInvalid={!!errors.zip && touched.zip}
                          {...fieldStyles}
                        />
                        {!!errors.zip && touched.zip && (
                          <FieldError>{errors.zip}</FieldError>
                        )}
                      </Col>
                    </Row>

                    <Row form>
                      <Col xs="12" sm="6">
                        <FieldLabel>E-Mail</FieldLabel>
                        <Input
                          as={Field}
                          type="email"
                          name="email"
                          id="email"
                          autoComplete="email"
                          isInvalid={!!errors.email && touched.email}
                          {...fieldStyles}
                        />
                        {!!errors.email && touched.email && (
                          <FieldError>{errors.email}</FieldError>
                        )}
                      </Col>
                      <Col xs="12" sm="6">
                        <FieldLabel>Telefon</FieldLabel>
                        <Input
                          as={Field}
                          type="text"
                          name="phone"
                          id="phone"
                          autoComplete="tel"
                          isInvalid={!!errors.phone && touched.phone}
                          {...fieldStyles}
                        />
                        {!!errors.phone && touched.phone && (
                          <FieldError>{errors.phone}</FieldError>
                        )}
                      </Col>
                    </Row>

                    <FieldLabel optional>Webseite</FieldLabel>
                    <Input
                      as={Field}
                      type="text"
                      name="website"
                      id="website"
                      isInvalid={!!errors.website && touched.website}
                      {...fieldStyles}
                    />
                    {!!errors.website && touched.website && (
                      <FieldError>{errors.website}</FieldError>
                    )}

                    <Box mt="26px">
                      <Button
                        type="submit"
                        colorScheme="red"
                        isDisabled={isSubmitting}
                        width="100%"
                        fontWeight={700}
                        fontSize="17px"
                        borderRadius="10px"
                        py="15px"
                        height="auto"
                      >
                        Kostenlosen Zugang anfragen
                      </Button>
                      <Text
                        fontSize="12px"
                        color="gray.400"
                        mt="12px"
                        textAlign="center"
                      >
                        Wir melden uns innerhalb eines Werktags. Kostenlos und
                        unverbindlich.
                      </Text>
                    </Box>

                    <Text
                      fontSize="12px"
                      color="gray.400"
                      lineHeight="1.5"
                      mt="18px"
                    >
                      Mit Klick auf „Kostenlosen Zugang anfragen“ akzeptieren
                      Sie unsere{' '}
                      <Link
                        to="https://talocasa.s3.eu-west-1.amazonaws.com/terms/20260823_AGB_HAUSGOLD_Maklerportal.pdf"
                        eventLabel="AGB für das Maklerportal"
                        target="_blank"
                      >
                        AGB für das Maklerportal
                      </Link>
                      , unsere{' '}
                      <Link
                        to="https://talocasa.s3.eu-west-1.amazonaws.com/terms/20260823_AGB_Servicevertrag_Autopilot.pdf"
                        eventLabel="AGB für den Autopilot-Service"
                        target="_blank"
                      >
                        AGB für den Autopilot-Service
                      </Link>{' '}
                      und unsere{' '}
                      <Link
                        to="/datenschutz/"
                        eventLabel="Datenschutzbestimmungen"
                        target="_blank"
                      >
                        Datenschutzbestimmungen
                      </Link>
                      .
                    </Text>
                  </FormikForm>
                )}
              </Formik>
            </Box>
          </Col>
        </Row>
      </Container>
    </Layout>
  );
};

export default Kooperation;

export const pageQuery = graphql`
  query KooperationQuery {
    site {
      siteMetadata {
        title
        siteUrl
      }
    }
    googleRating {
      rating
      userRatingCount
      googleMapsUri
    }
    contentfulPage(slug: { eq: "koop-anfrage" }) {
      id
      title
      slug
      headline
      metaTitle
      metaDescription
      subTitle
      canonical
      indexable
      heroImage {
        gatsbyImageData(
          layout: FIXED
          width: 1920
          height: 500
          cropFocus: FACES
          resizingBehavior: FILL
        )
        title
      }
    }
  }
`;
